import { useState } from 'react';
import { Link } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import SEOJsonLd from '@/components/base/SEOJsonLd';

const partnerTypes = [
  {
    icon: 'ri-store-2-line',
    title: 'New Brand Startups',
    desc: 'We support new teams starting from scratch — from formula development and packaging design to small-batch trial production, helping you build your own brand quickly.',
    tags: ['Low MOQ', 'Formula R&D', 'Brand Building'],
    color: '#4CAF50',
  },
  {
    icon: 'ri-shopping-bag-line',
    title: 'E-Commerce Brands',
    desc: 'Fast lead times and flexible order quantities to meet the dynamic needs of e-commerce. We help create hot-selling products for online platforms.',
    tags: ['Fast Lead Time', 'Flexible MOQ', 'E-Commerce Hits'],
    color: '#66BB6A',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Pharmacy Channels',
    desc: 'We understand pharmacy channel regulatory requirements and provide products that meet health supplement regulations, strengthening your channel competitiveness.',
    tags: ['Regulatory Compliant', 'Channel Ready', 'Quality Assured'],
    color: '#81C784',
  },
  {
    icon: 'ri-global-line',
    title: 'International Brands',
    desc: 'With extensive multi-country export experience, we help international brands expand into or out of Taiwan with products meeting export regulatory requirements.',
    tags: ['Multi-Country Export', 'Regulatory Support', 'Global Markets'],
    color: '#A5D6A7',
  },
];

const suitableList = [
  'Want to build your own health supplement brand but lack production facilities',
  'Already have a brand and need a stable, high-quality manufacturing partner',
  'Want to launch new products quickly and shorten development timelines',
  'Need multiple dosage form options to meet different product line requirements',
  'Looking for one-stop regulatory submission and label design services',
  'Planning to expand overseas and need products meeting export regulations',
  'Have a unique formula concept and need a professional team to bring it to life',
  'Need to start with small-batch trial production and gradually scale up',
];

const notSuitable = [
  'Medical device or pharmaceutical drug manufacturing (outside our service scope)',
];

const steps = [
  {
    num: '01', icon: 'ri-discuss-line', title: 'Initial Consultation',
    desc: 'Fill out the online form or contact our sales team with your product requirements, target market, and budget. We\'ll respond promptly.',
    duration: 'Prompt response',
  },
  {
    num: '02', icon: 'ri-file-list-3-line', title: 'Requirements Confirmation',
    desc: 'Our account manager conducts an in-depth discussion to confirm product type, dosage form, formula direction, packaging specs, and regulatory needs.',
    duration: '3–5 business days',
  },
  {
    num: '03', icon: 'ri-draft-line', title: 'Formula Proposal',
    desc: 'Our R&D team submits a formula proposal including ingredient details, efficacy claims, reference specifications, and preliminary pricing.',
    duration: '5–10 business days',
  },
  {
    num: '04', icon: 'ri-test-tube-line', title: 'Sample Production',
    desc: 'After formula confirmation, we produce a small batch for client review of product quality, appearance, and taste. Adjustments can be made at this stage.',
    duration: '2–4 weeks',
  },
  {
    num: '05', icon: 'ri-file-check-line', title: 'Contract Signing',
    desc: 'Upon sample approval, we formally sign the cooperation agreement confirming order quantity, packaging design, lead time, and payment terms.',
    duration: '1–2 weeks',
  },
  {
    num: '06', icon: 'ri-settings-3-line', title: 'Mass Production',
    desc: 'Per the contract, full-scale production begins with quality management monitoring throughout every process step to ensure finished products match sample specs.',
    duration: '4–8 weeks',
  },
  {
    num: '07', icon: 'ri-checkbox-circle-line', title: 'QC Inspection',
    desc: 'After production, comprehensive quality testing is conducted and a full batch inspection report is issued, verifying safety compliance before shipment.',
    duration: '3–5 business days',
  },
  {
    num: '08', icon: 'ri-truck-line', title: 'Delivery & Shipment',
    desc: 'Products are shipped per the agreed schedule with complete shipping documentation. Ongoing support is available for market feedback tracking.',
    duration: 'As agreed',
  },
];

const modes = [
  {
    mode: 'ODM',
    fullName: 'Original Design Manufacturing',
    title: 'ODM — Commissioned Design & Manufacturing',
    color: '#4CAF50',
    bgColor: '#f0faf0',
    borderColor: '#c8e6c9',
    desc: 'Kang Yong provides complete formula design, ingredient selection, and product specifications. Clients only need to provide brand positioning and target claims to receive a fully manufactured product.',
    suitable: 'Ideal for new brands or channel partners looking to launch quickly',
    points: [
      'Kang Yong provides existing or custom formula options',
      'R&D team adjusts formulas based on your requirements',
      'Includes full sourcing, production, and quality management',
      'Private label packaging for your own brand',
      'Shorter product development timeline',
    ],
  },
  {
    mode: 'OEM',
    fullName: 'Original Equipment Manufacturing',
    title: 'OEM — Commissioned Manufacturing',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    borderColor: '#a5d6a7',
    desc: 'Clients provide complete formula specifications and technical documents. Kang Yong handles manufacturing to ensure products are produced precisely according to client requirements with stable, consistent quality.',
    suitable: 'Ideal for established brands with existing formulas seeking a reliable manufacturer',
    points: [
      'Client provides complete formula and specifications',
      'Kang Yong manufactures according to your specs',
      'Strict quality control ensures batch-to-batch consistency',
      'NDA agreements protect client formula confidentiality',
      'Support for multiple dosage forms and packaging specs',
    ],
  },
];

const advantages = [
  { icon: 'ri-flask-line', title: 'Formula Development Support', desc: 'Professional R&D team assists with formula design — full technical support from efficacy claims to ingredient selection.' },
  { icon: 'ri-price-tag-3-line', title: 'Competitive Pricing', desc: 'Proprietary facility and raw material procurement advantages allow us to offer industry-competitive OEM pricing to reduce brand costs.' },
  { icon: 'ri-time-line', title: 'Fast Lead Times', desc: 'Effective production scheduling management shortens time-to-market from sample to mass production.' },
  { icon: 'ri-shield-check-line', title: 'Quality Assurance', desc: 'Every batch comes with a complete quality management report. GMP facility production ensures stable, guaranteed quality.' },
  { icon: 'ri-file-text-line', title: 'Regulatory Support', desc: 'Expertise in health supplement regulations, supporting product label review and regulatory submissions for legal market entry.' },
  { icon: 'ri-lock-line', title: 'Confidentiality & Security', desc: 'Strict NDA confidentiality agreements signed upfront. Your formula and business information are fully protected.' },
];

const faqs = [
  {
    q: 'What is the minimum order quantity (MOQ)?',
    a: 'MOQ varies depending on dosage form and formula. Capsule and tablet products typically have an MOQ of approximately 3,000–5,000 bottles; powder packs and beverages start from around 5,000 units. Please contact our sales team to confirm your specific requirements.',
  },
  {
    q: 'I only have a product concept but no formula — can we still collaborate?',
    a: 'Absolutely! Kang Yong provides full ODM services. Simply share your brand positioning, target audience, and efficacy claims, and our R&D team will develop a custom formula for you — no technical background required.',
  },
  {
    q: 'Will my formula be protected during the collaboration?',
    a: "Yes. We sign a strict NDA (Non-Disclosure Agreement) before collaboration begins. Your formula, business plans, and all technical information are fully protected and will never be shared.",
  },
  {
    q: 'How long does it take from sample confirmation to mass production?',
    a: 'From initial consultation to sample completion typically takes 3–6 weeks. After sample approval, production takes approximately 4–8 weeks depending on order volume and product complexity.',
  },
  {
    q: 'Can you assist with packaging design and label review?',
    a: 'Yes. Kang Yong offers packaging design recommendations and label content review services, helping clients ensure compliance with Taiwan health supplement regulations for legal product launch.',
  },
  {
    q: 'How is product quality assured after collaboration begins?',
    a: 'Every production batch undergoes comprehensive quality testing including microbiology, heavy metals, disintegration, and other safety indicators. A complete batch inspection report is issued with every order to ensure all products meet specification standards.',
  },
  {
    q: 'Can you handle export for my products?',
    a: "Yes. Kang Yong has extensive multi-country export experience and is familiar with regulatory differences across markets. We assist with preparing export documentation and confirming products meet target market requirements.",
  },
  {
    q: 'How do I get started for the first time?',
    a: 'The easiest way is to contact our sales team via the website contact form, LINE, or phone with your initial requirements. Our account manager will respond promptly and arrange a follow-up discussion.',
  },
];

const contactOptions = [
  {
    icon: 'ri-chat-1-line', label: 'LINE Inquiry',
    desc: 'Scan the QR code or click the link to add us on LINE and chat directly with our team.',
    action: 'Add on LINE', href: 'https://lin.ee/X7Hcpf4', color: '#4CAF50', bg: '#f0faf0',
  },
  {
    icon: 'ri-phone-line', label: 'Phone Consultation',
    desc: 'Monday–Friday 09:00–18:00, our team is ready to take your inquiry.',
    action: 'Call Us', href: 'tel:04-22029858', color: '#2e7d32', bg: '#e8f5e9',
  },
  {
    icon: 'ri-mail-line', label: 'Email Inquiry',
    desc: "Send us your requirements and we'll reply with a quote as soon as possible.",
    action: 'Send Email', href: 'mailto:natural1070606@gmail.com', color: '#388e3c', bg: '#f1f8e9',
  },
];

export default function EnPartnershipPage() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const siteUrl = (window as any).__VITE_SITE_URL__ || 'https://kangyong.com';
  useSEO({
    title: 'OEM/ODM Partnership | Kang Yong Biotech Taiwan',
    description: 'Start your health supplement OEM/ODM partnership with Kang Yong Biotech. Low MOQ, fast lead times, GMP-certified production, and full regulatory support for brands worldwide.',
    keywords: 'OEM ODM partnership Taiwan, health supplement manufacturing partner, low MOQ supplement, GMP contract manufacturing',
    lang: 'en',
    canonical: 'https://kangyong.com/en/partnership',
  });

  return (
    <EnLayout>
      <SEOJsonLd type="WebPage" pageTitle="OEM/ODM Partnership | Kang Yong Biotech Taiwan" pageDescription="Start your health supplement OEM/ODM partnership with Kang Yong Biotech. Low MOQ, fast lead times, GMP-certified production." pageUrl={`${siteUrl}/en/partnership`} />
      {/* Hero */}
      <section className="relative pt-20 min-h-[440px] md:min-h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20aerial%20view%20of%20lush%20dense%20deep%20green%20tropical%20forest%20canopy%20with%20dramatic%20mist%20and%20moody%20low%20light%2C%20rich%20dark%20emerald%20tones%20with%20deep%20shadows%2C%20cinematic%20nature%20landscape%20perfect%20for%20text%20overlay%20contrast&width=1920&height=500&seq=partner-hero-bg-03&orientation=landscape"
            alt="Partnership with Kang Yong"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/92 via-[#0d2210]/72 to-[#0d2210]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/75 via-transparent to-[#0d2210]/30" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-16 pt-28">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 tracking-wide">
              Partnership
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-5">
              <span className="block">Launch a Partnership,</span>
              <span className="block text-green-300">Unlock New Brand Growth</span>
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl mb-8">
              Whether you're a startup brand, e-commerce channel, or traditional retailer, Kang Yong Biotech offers the most suitable ODM/OEM manufacturing partnership to get your products to market fast.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/en/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                Inquire Now
              </Link>
              <Link to="/en/services" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Partnership Types
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Who Do We Serve?</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong Biotech serves a wide range of clients — no matter what stage you're at, we have the right OEM/ODM solution for you.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerTypes.map((p) => (
              <div key={p.title} className="bg-[#f7faf7] rounded-2xl p-7 hover:border-green-200 border border-transparent hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl mb-5" style={{ backgroundColor: `${p.color}20` }}>
                  <i className={`${p.icon} text-2xl`} style={{ color: p.color }} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-3">{p.title}</h3>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75] mb-4">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.tags.map((tag) => (
                    <span key={tag} className="text-[0.75rem] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${p.color}15`, color: p.color }}>{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable For */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                Who It's For
              </span>
              <h2 className="text-[#1a2e1a] font-bold leading-tight mb-6">
                Which Brands Are the Right<br />Fit for Kang Yong?
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">
                If any of the following describes you, Kang Yong Biotech is your ideal manufacturing partner:
              </p>
              <div className="space-y-3 mb-8">
                {suitableList.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.9375rem] leading-[1.75]">{item}</span>
                  </div>
                ))}
              </div>
              {notSuitable.length > 0 && (
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-5">
                  <p className="text-orange-700 font-semibold text-[0.9375rem] mb-2 flex items-center gap-2">
                    <i className="ri-information-line text-lg" /> Service Scope Note
                  </p>
                  {notSuitable.map((item) => (
                    <p key={item} className="text-orange-600 text-[0.875rem] leading-[1.75]">· {item}</p>
                  ))}
                </div>
              )}
              <div className="mt-8">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Ask If We're a Good Fit <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20bright%20supplement%20manufacturing%20facility%20interior%20with%20large%20windows%20shelves%20full%20of%20products%2C%20clean%20professional%20health%20food%20production%20environment%2C%20workers%20in%20white%20uniforms%2C%20natural%20daylight&width=720&height=540&seq=suitable-img-01&orientation=landscape"
                  alt="Suitable Partners"
                  className="w-full h-[360px] lg:h-[460px] object-cover object-top rounded-2xl"
                />
                <div className="absolute bottom-5 left-5 bg-white/95 rounded-xl px-5 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-lg">
                    <i className="ri-user-heart-line text-[#4CAF50] text-xl" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">250+ Brand Partners</p>
                    <p className="text-gray-400 text-[0.75rem]">Across all types of partners</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation Process — 8 Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Cooperation Process
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Eight Steps, from Consultation to Delivery</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              A clear, transparent process so you're always in control of your product's progress at every stage.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, idx) => (
              <div key={s.num} className="relative bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] leading-none opacity-30">{s.num}</span>
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-green-100">
                    <i className={`${s.icon} text-[#4CAF50] text-lg`} />
                  </div>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{s.desc}</p>
                <div className="flex items-center gap-1.5">
                  <i className="ri-time-line text-[#4CAF50] text-sm" />
                  <span className="text-[#4CAF50] text-[0.8125rem] font-medium">{s.duration}</span>
                </div>
                {idx < steps.length - 1 && (idx + 1) % 4 !== 0 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center z-10">
                    <i className="ri-arrow-right-s-line text-green-300 text-xl" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Start Step One — Inquire Now <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ODM vs OEM */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Collaboration Mode
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">ODM vs OEM — Choose the Right Model for You</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong offers two primary collaboration models — choose flexibly based on your needs and brand development stage.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {modes.map((m) => (
              <div key={m.mode} className="rounded-2xl p-8 border-2 transition-all duration-300" style={{ backgroundColor: m.bgColor, borderColor: m.borderColor }}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="inline-block text-white font-bold text-[1.375rem] px-5 py-2 rounded-xl mb-3" style={{ backgroundColor: m.color }}>{m.mode}</span>
                    <p className="text-gray-400 text-[0.8125rem]">{m.fullName}</p>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.25rem] mt-2">{m.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-5">{m.desc}</p>
                <div className="inline-block text-[0.8125rem] font-medium px-3 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${m.color}18`, color: m.color }}>
                  ✓ {m.suitable}
                </div>
                <ul className="space-y-2.5">
                  {m.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-lg" style={{ color: m.color }} />
                      </div>
                      <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-2">Not Sure Which Model Fits You?</h3>
                <p className="text-gray-500 text-[0.9375rem]">Contact our sales team and we'll recommend the most suitable collaboration approach based on your needs.</p>
              </div>
              <Link to="/en/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0">
                Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Partnership Advantages
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Six Advantages of Partnering with Kang Yong</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Choose Kang Yong Biotech for a smoother, more confident OEM/ODM journey.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((a) => (
              <div key={a.title} className="flex gap-5 p-6 rounded-2xl bg-[#f7faf7] hover:bg-green-50 border border-transparent hover:border-green-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl flex-shrink-0 border border-green-100">
                  <i className={`${a.icon} text-[#4CAF50] text-xl`} />
                </div>
                <div>
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{a.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — 8 Questions */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              FAQ
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Frequently Asked Questions Before Partnering</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              We've compiled the most common questions about working with us. Feel free to contact us for anything else.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${openIdx === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}>
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                >
                  <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openIdx === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openIdx === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                    <i className={`text-sm ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden mb-12">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20lush%20green%20forest%20landscape%20from%20above%2C%20vibrant%20rich%20emerald%20green%20nature%2C%20wellness%20health%20concept%20background%2C%20beautiful%20natural%20texture%20pattern&width=1400&height=400&seq=partner-cta-bg-01&orientation=landscape"
                alt="Partner CTA Background"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-[#1a3a1a]/80" />
            </div>
            <div className="relative z-10 px-8 lg:px-14 py-14 text-center">
              <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
                Start Collaborating
              </span>
              <h2 className="text-white font-bold leading-tight mb-5">
                Ready to Get Started?<br />Let's Turn Your Idea into a Product
              </h2>
              <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-8">
                Regardless of the size of your project, Kang Yong's team takes every collaboration opportunity seriously and provides the most professional advice and service.
              </p>
              <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                Fill Out the Inquiry Form <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {contactOptions.map((opt) => (
              <div key={opt.label} className="rounded-2xl p-7 border border-gray-100 transition-all duration-300 hover:border-green-200 hover:-translate-y-1" style={{ backgroundColor: opt.bg }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-5" style={{ backgroundColor: `${opt.color}20` }}>
                  <i className={`${opt.icon} text-2xl`} style={{ color: opt.color }} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{opt.label}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-5">{opt.desc}</p>
                <a href={opt.href} rel="nofollow" className="whitespace-nowrap inline-flex items-center gap-2 font-semibold text-[0.9375rem] transition-colors duration-200 cursor-pointer" style={{ color: opt.color }}>
                  {opt.action} <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </EnLayout>
  );
}
